import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { MessageSquarePlus, X, Bug, Lightbulb, MessageCircle, Star, MessagesSquare, ChevronRight } from "lucide-react";
import { useFeaturebase } from "featurebase-js/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/App";

const TYPES = [
  { key: "bug",     icon: Bug,          labelKey: "feedback.typeBug" },
  { key: "feature", icon: Lightbulb,    labelKey: "feedback.typeFeature" },
  { key: "general", icon: MessageCircle, labelKey: "feedback.typeGeneral" },
];

// Hidden on full-bleed video screens, where a floating button covers the stream itself.
const HIDDEN_PATHS = ["/welcome", "/login", "/onboarding", "/paywall", "/payment", "/stream/", "/go-live", "/live"];

export default function FeedbackWidget() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const { show: showMessenger, unreadCount } = useFeaturebase();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("general");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [messageError, setMessageError] = useState(false);

  const hidden = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  if (!isAuthenticated || hidden) return null;

  const reset = () => {
    setType("general");
    setMessage("");
    setRating(0);
    setHoverRating(0);
    setDone(false);
    setMessageError(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      setMessageError(true);
      return;
    }
    setMessageError(false);
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        type,
        message: message.trim(),
        rating: rating || null,
        page: location.pathname,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 left-4 z-40 w-12 h-12 rounded-full bg-victory-lime text-victory-bg flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
        aria-label={unreadCount > 0 ? `${t("feedback.title")} (${unreadCount} unread)` : t("feedback.title")}
      >
        <MessageSquarePlus className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-victory-danger text-victory-text text-[10px] font-mono font-bold flex items-center justify-center border-2 border-victory-bg">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={handleClose}
        />
      )}

      {/* Sheet */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 bg-victory-card border-t border-victory-border rounded-t-2xl p-5 space-y-4 transition-transform duration-300 ${open ? "translate-y-0" : "translate-y-full"}`}>
        {done ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="w-14 h-14 rounded-full bg-victory-lime/20 flex items-center justify-center">
              <Star className="w-7 h-7 text-victory-lime" />
            </div>
            <p className="font-heading font-bold text-victory-text text-lg">{t("feedback.thanks")}</p>
            <p className="text-victory-muted text-sm text-center">{t("feedback.thanksDesc")}</p>
            <button onClick={handleClose} className="victory-btn-primary mt-2 px-8">
              {t("common.back")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-victory-text">{t("feedback.title")}</h2>
              <button onClick={handleClose} aria-label="Close" className="w-11 h-11 flex items-center justify-center touch-target rounded-full bg-victory-border text-victory-muted">
                <X className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => { handleClose(); showMessenger(); }}
              className="w-full touch-target flex items-center gap-3 rounded-xl bg-victory-bg border border-victory-border px-3 text-left hover:border-victory-lime/40 transition-colors"
            >
              <MessagesSquare className="w-4 h-4 text-victory-lime flex-shrink-0" />
              <span className="flex-1 text-victory-text text-sm">{t("feedback.chat", "Chat with the team")}</span>
              {unreadCount > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-victory-danger text-victory-text text-[10px] font-mono font-bold flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-victory-muted" />
            </button>

            {/* Type selector */}
            <div className="flex gap-2">
              {TYPES.map(({ key, icon: Icon, labelKey }) => (
                <button
                  key={key}
                  onClick={() => setType(key)}
                  className={`flex-1 touch-target flex items-center justify-center gap-1.5 rounded-xl text-sm font-medium transition-colors ${
                    type === key ? "bg-victory-lime text-victory-bg" : "bg-victory-bg border border-victory-border text-victory-muted"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t(labelKey)}
                </button>
              ))}
            </div>

            {/* Star rating */}
            <div>
              <p className="text-victory-muted text-xs mb-1.5">{t("feedback.ratingLabel")}</p>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(star === rating ? 0 : star)}
                    aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                    aria-pressed={rating >= star}
                    className="text-2xl leading-none transition-colors touch-target flex items-center justify-center"
                  >
                    <Star
                      className={`w-7 h-7 ${
                        (hoverRating || rating) >= star
                          ? "fill-victory-lime text-victory-lime"
                          : "fill-transparent text-victory-border"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="victory-label">{t("feedback.messageLabel")}</label>
              <textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); if (e.target.value.trim()) setMessageError(false); }}
                className={`victory-input resize-none ${messageError ? "border-red-500" : ""}`}
                rows={3}
                placeholder={t("feedback.messagePlaceholder")}
                maxLength={1000}
              />
              {messageError && (
                <p className="text-red-400 text-xs mt-1">{t("feedback.messageRequired")}</p>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="victory-btn-primary w-full disabled:opacity-40"
            >
              {submitting ? t("feedback.sending") : t("feedback.send")}
            </button>
          </>
        )}
      </div>
    </>
  );
}
